// ============================================================================
// RAM SIMULATION ENGINE — Monte Carlo RBD-Based Production Availability
// Equivalent to Isograph AvSim / DNV GL OPTAGON
// Runs N lifecycle simulations against an RBD, tracking failures/repairs/
// production hour-by-hour with full statistical analysis
// ============================================================================

// ---- Random variate generators ----
function randExp(lambda) {
  // Exponential distribution: mean = 1/lambda
  if (lambda <= 0) return Infinity;
  return -Math.log(1 - Math.random()) / lambda;
}

function randWeibull(beta, eta) {
  // Weibull distribution: shape=beta, scale=eta
  if (beta <= 0 || eta <= 0) return Infinity;
  return eta * Math.pow(-Math.log(1 - Math.random()), 1 / beta);
}

function randLognormal(mu, sigma) {
  // Lognormal: mu=location, sigma=shape (of underlying normal)
  // Box-Muller transform
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return Math.exp(mu + sigma * z);
}

function randNormal(mean, stddev) {
  const u1 = Math.random();
  const u2 = Math.random();
  const z = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
  return mean + stddev * z;
}

function sampleTTF(block) {
  // Time to failure from block's distribution parameters
  const dist = block.distribution || 'exponential';
  const mtbf = block.mtbf || 0;
  if (mtbf <= 0) return Infinity; // never fails

  if (dist === 'weibull' && block.beta && block.eta) {
    return randWeibull(block.beta, block.eta);
  }
  if (dist === 'lognormal') {
    // Convert MTBF to lognormal params (sigma defaults to 0.8 for mechanical)
    const sigma = block.lognormalSigma || 0.8;
    const mu = Math.log(mtbf) - sigma * sigma / 2;
    return randLognormal(mu, sigma);
  }
  // Default: exponential
  return randExp(1 / mtbf);
}

function sampleTTR(block) {
  // Time to repair — lognormal by default (repairs skew long, per Optimal methodology)
  const mttr = block.mttr || 0;
  if (mttr <= 0) return 0;

  const repairDist = block.repairDistribution || 'lognormal';
  if (repairDist === 'lognormal') {
    const sigma = block.repairSigma || 0.6;
    const mu = Math.log(mttr) - sigma * sigma / 2;
    return Math.max(0.5, randLognormal(mu, sigma));
  }
  if (repairDist === 'exponential') {
    return Math.max(0.5, randExp(1 / mttr));
  }
  // Fixed
  return mttr;
}

// ---- RBD Topology Evaluation ----
// Given block states (operating/failed), compute system output capacity
function evaluateRBD(blocks, connections, blockStates) {
  // Build graph: find paths from sources to sinks
  // A block's effective capacity = its outputCapacity if operating, 0 if failed
  // Series: min capacity along path
  // Parallel: max capacity across paths
  // K-of-N: sum of top-K capacities from N redundant items
  // Standby: primary, then standby if primary failed

  if (!blocks || blocks.length === 0) return 100;

  // Build adjacency
  const adj = new Map();
  const inDeg = new Map();
  const allIds = new Set();

  blocks.forEach(b => {
    adj.set(b.id, []);
    inDeg.set(b.id, 0);
    allIds.add(b.id);
  });

  connections.forEach(c => {
    if (c.from !== 'START' && c.to !== 'END' && adj.has(c.from) && allIds.has(c.to)) {
      adj.get(c.from).push(c.to);
      inDeg.set(c.to, (inDeg.get(c.to) || 0) + 1);
    }
  });

  // Find source blocks (no incoming) and sink blocks (no outgoing)
  const sources = [];
  const sinks = [];
  blocks.forEach(b => {
    if ((inDeg.get(b.id) || 0) === 0) sources.push(b.id);
    if ((adj.get(b.id) || []).length === 0) sinks.push(b.id);
  });

  // Also check for START/END connections
  connections.forEach(c => {
    if (c.from === 'START' && allIds.has(c.to) && !sources.includes(c.to)) sources.push(c.to);
    if (c.to === 'END' && allIds.has(c.from) && !sinks.includes(c.from)) sinks.push(c.from);
  });

  if (sources.length === 0) sources.push(blocks[0].id);
  if (sinks.length === 0) sinks.push(blocks[blocks.length - 1].id);

  // Get block capacity
  const getCapacity = (blockId) => {
    const state = blockStates.get(blockId);
    if (!state || state.status === 'failed') return 0;
    if (state.status === 'degraded') return state.degradedCapacity || 50;
    const block = blocks.find(b => b.id === blockId);
    return block?.capacity || 100;
  };

  // For parallel groups: detect parallel blocks (same predecessors and successors)
  // Simple approach: traverse from each source, accumulating capacity
  // For a proper RBD: evaluate each path, then combine based on topology

  // Simplified but effective approach:
  // 1. Topological sort
  // 2. For each layer, check if blocks are parallel (shared pred/succ)
  // 3. Series layers: take min
  // 4. Parallel layers: take max (or k-of-n sum)

  const layers = [];
  const layerMap = new Map();
  const queue = [...sources];
  const visited = new Set();

  while (queue.length > 0) {
    const layer = [...queue];
    layers.push(layer);
    layer.forEach(id => { layerMap.set(id, layers.length - 1); visited.add(id); });
    queue.length = 0;
    layer.forEach(id => {
      (adj.get(id) || []).forEach(child => {
        inDeg.set(child, inDeg.get(child) - 1);
        if (inDeg.get(child) === 0 && !visited.has(child)) queue.push(child);
      });
    });
  }

  // Any unvisited blocks go into their own layer
  blocks.forEach(b => {
    if (!visited.has(b.id)) {
      layers.push([b.id]);
      layerMap.set(b.id, layers.length - 1);
    }
  });

  // Evaluate each layer
  let systemCapacity = 100;
  layers.forEach(layer => {
    if (layer.length === 1) {
      // Series element
      const cap = getCapacity(layer[0]);
      systemCapacity = Math.min(systemCapacity, cap);
    } else {
      // Parallel / redundant group
      const caps = layer.map(id => getCapacity(id)).sort((a, b) => b - a);
      const block0 = blocks.find(b => b.id === layer[0]);
      const blockType = block0?.blockType || 'parallel';

      if (blockType === 'kofn') {
        const k = block0?.kRequired || 1;
        // Sum of top-k capacities, averaged
        const topK = caps.slice(0, k);
        const kCap = topK.length > 0 ? topK.reduce((s, c) => s + c, 0) / k : 0;
        systemCapacity = Math.min(systemCapacity, kCap);
      } else if (blockType === 'standby') {
        // Best available capacity (primary first, then standbys)
        const bestCap = caps[0] || 0;
        systemCapacity = Math.min(systemCapacity, bestCap);
      } else {
        // Parallel: max capacity
        const maxCap = caps[0] || 0;
        systemCapacity = Math.min(systemCapacity, maxCap);
      }
    }
  });

  return systemCapacity;
}

// ---- Planned Maintenance Scheduler ----
function generatePMEvents(blocks, missionTime) {
  const events = [];
  blocks.forEach(block => {
    if (block.pmInterval && block.pmInterval > 0 && block.pmDuration && block.pmDuration > 0) {
      let t = block.pmOffset || block.pmInterval;
      while (t < missionTime) {
        events.push({
          blockId: block.id,
          type: 'pm_start',
          time: t,
          duration: block.pmDuration,
          label: block.pmLabel || 'Planned Maintenance'
        });
        t += block.pmInterval;
      }
    }
    // Full shutdown events
    if (block.shutdownStart != null && block.shutdownDuration) {
      events.push({
        blockId: block.id,
        type: 'shutdown_start',
        time: block.shutdownStart,
        duration: block.shutdownDuration,
        label: block.shutdownLabel || 'Full Shutdown'
      });
    }
  });
  return events.sort((a, b) => a.time - b.time);
}

// ---- Single Lifecycle Simulation ----
function simulateLifecycle(blocks, connections, config) {
  const {
    missionTime = 8760,         // hours (default 1 year)
    targetProduction = 100,      // units per hour at full capacity
    productionUnit = 'bbl',
    logisticDelayConfig = null,  // { probability: 0.1, durations: [24, 168, 720] }
    commonCauseBeta = 0.1,       // β-factor for common cause failures
    beanUpTime = 0,              // hours to ramp up after full shutdown
    deferredEffects = {},        // { blockId: delayHours }
    fullShutdowns = [],          // [{ start: hours, duration: hours, label: 'September Shutdown' }]
    timeStep = 1                 // simulation resolution in hours
  } = config;

  // Initialise block states
  const blockStates = new Map();
  const blockTTF = new Map();    // next time to failure
  const blockTTR = new Map();    // time repair completes
  const blockHistory = new Map(); // { blockId: [{ start, end, type: 'failure'|'pm'|'shutdown' }] }

  blocks.forEach(b => {
    blockStates.set(b.id, { status: 'operating', capacity: b.capacity || 100 });
    blockTTF.set(b.id, sampleTTF(b));
    blockTTR.set(b.id, Infinity);
    blockHistory.set(b.id, []);
  });

  // Generate PM events
  const pmEvents = generatePMEvents(blocks, missionTime);
  let pmIdx = 0;

  // Tracking arrays
  const hourlyProduction = new Array(Math.ceil(missionTime / timeStep)).fill(0);
  const hourlyCapacity = new Array(Math.ceil(missionTime / timeStep)).fill(0);
  const failureLog = []; // { blockId, time, duration, type }
  const shortfallByBlock = new Map();
  const failureCountByBlock = new Map();
  const downtimeByBlock = new Map();

  blocks.forEach(b => {
    shortfallByBlock.set(b.id, 0);
    failureCountByBlock.set(b.id, 0);
    downtimeByBlock.set(b.id, 0);
  });

  let totalProduction = 0;
  let totalTarget = 0;
  let fullShutdownActive = false;
  let beanUpRemaining = 0;

  // Check if in full shutdown period
  const isInShutdown = (t) => {
    for (const sd of fullShutdowns) {
      if (t >= sd.start && t < sd.start + sd.duration) return sd;
    }
    return null;
  };

  // Main simulation loop
  for (let step = 0; step < hourlyProduction.length; step++) {
    const t = step * timeStep;

    // Check full shutdowns
    const shutdown = isInShutdown(t);
    if (shutdown) {
      hourlyProduction[step] = 0;
      hourlyCapacity[step] = 0;
      totalTarget += targetProduction * timeStep;
      fullShutdownActive = true;
      continue;
    }

    // Handle bean-up after shutdown
    if (fullShutdownActive) {
      beanUpRemaining = beanUpTime;
      fullShutdownActive = false;
      // Restart all blocks after shutdown (reliability renewal)
      blocks.forEach(b => {
        blockStates.set(b.id, { status: 'operating', capacity: b.capacity || 100 });
        blockTTF.set(b.id, t + sampleTTF(b));
        blockTTR.set(b.id, Infinity);
      });
    }

    // Process PM events at this timestep
    while (pmIdx < pmEvents.length && pmEvents[pmIdx].time <= t) {
      const pm = pmEvents[pmIdx];
      const bs = blockStates.get(pm.blockId);
      if (bs && bs.status === 'operating') {
        bs.status = 'maintenance';
        blockTTR.set(pm.blockId, t + pm.duration);
        blockHistory.get(pm.blockId)?.push({ start: t, end: t + pm.duration, type: 'pm' });
        downtimeByBlock.set(pm.blockId, (downtimeByBlock.get(pm.blockId) || 0) + pm.duration);
      }
      pmIdx++;
    }

    // Process failures and repairs for each block
    blocks.forEach(b => {
      const state = blockStates.get(b.id);
      if (!state) return;

      // Check if repair completes at this step
      if ((state.status === 'failed' || state.status === 'maintenance') && blockTTR.get(b.id) <= t) {
        state.status = 'operating';
        state.capacity = b.capacity || 100;
        // Schedule next failure from this point
        blockTTF.set(b.id, t + sampleTTF(b));
        blockTTR.set(b.id, Infinity);
      }

      // Check if failure occurs at this step
      if (state.status === 'operating' && blockTTF.get(b.id) <= t) {
        state.status = 'failed';
        state.capacity = 0;
        failureCountByBlock.set(b.id, (failureCountByBlock.get(b.id) || 0) + 1);

        // Sample repair time
        let repairTime = sampleTTR(b);

        // Add logistic delay
        let logDelay = 0;
        if (logisticDelayConfig && Math.random() < (logisticDelayConfig.probability || 0)) {
          const delays = logisticDelayConfig.durations || [24];
          logDelay = delays[Math.floor(Math.random() * delays.length)];
        }
        // Block-specific logistic delay
        if (b.logisticDelay && b.logisticDelay > 0) {
          logDelay = Math.max(logDelay, b.logisticDelay);
        }

        const totalDowntime = repairTime + logDelay;
        blockTTR.set(b.id, t + totalDowntime);
        downtimeByBlock.set(b.id, (downtimeByBlock.get(b.id) || 0) + totalDowntime);

        failureLog.push({ blockId: b.id, time: t, duration: totalDowntime, type: 'failure', logisticDelay: logDelay });
        blockHistory.get(b.id)?.push({ start: t, end: t + totalDowntime, type: 'failure' });

        // Common cause failure: if block has redundant peers, small chance of taking out peer
        if (commonCauseBeta > 0 && b.redundantGroup) {
          blocks.filter(peer => peer.redundantGroup === b.redundantGroup && peer.id !== b.id).forEach(peer => {
            if (Math.random() < commonCauseBeta) {
              const peerState = blockStates.get(peer.id);
              if (peerState && peerState.status === 'operating') {
                peerState.status = 'failed';
                peerState.capacity = 0;
                const peerRepair = sampleTTR(peer);
                blockTTR.set(peer.id, t + peerRepair);
                downtimeByBlock.set(peer.id, (downtimeByBlock.get(peer.id) || 0) + peerRepair);
                failureCountByBlock.set(peer.id, (failureCountByBlock.get(peer.id) || 0) + 1);
                failureLog.push({ blockId: peer.id, time: t, duration: peerRepair, type: 'common_cause' });
                blockHistory.get(peer.id)?.push({ start: t, end: t + peerRepair, type: 'common_cause' });
              }
            }
          });
        }
      }
    });

    // Evaluate system capacity from RBD topology
    let capacity = evaluateRBD(blocks, connections, blockStates);

    // Apply deferred effects: some failures don't immediately impact production
    // (simplified: if block just failed, check if it has a deferred effect time)
    // Full implementation would track deferred timers per block

    // Apply bean-up ramp
    if (beanUpRemaining > 0) {
      const rampFraction = 1 - (beanUpRemaining / beanUpTime);
      capacity = capacity * rampFraction;
      beanUpRemaining = Math.max(0, beanUpRemaining - timeStep);
    }

    const production = targetProduction * (capacity / 100) * timeStep;
    hourlyProduction[step] = production;
    hourlyCapacity[step] = capacity;
    totalProduction += production;
    totalTarget += targetProduction * timeStep;

    // Attribute shortfall to failed blocks
    if (capacity < 100) {
      const shortfall = targetProduction * ((100 - capacity) / 100) * timeStep;
      // Distribute shortfall to currently-failed blocks proportionally
      const failedBlocks = blocks.filter(b => {
        const s = blockStates.get(b.id);
        return s && s.status === 'failed';
      });
      if (failedBlocks.length > 0) {
        const perBlock = shortfall / failedBlocks.length;
        failedBlocks.forEach(fb => {
          shortfallByBlock.set(fb.id, (shortfallByBlock.get(fb.id) || 0) + perBlock);
        });
      }
    }
  }

  // Compute shutdown shortfall
  let shutdownShortfall = 0;
  fullShutdowns.forEach(sd => {
    shutdownShortfall += targetProduction * sd.duration;
  });

  return {
    totalProduction,
    totalTarget,
    productionAvailability: totalTarget > 0 ? totalProduction / totalTarget : 1,
    shortfall: totalTarget - totalProduction,
    shutdownShortfall,
    hourlyProduction,
    hourlyCapacity,
    failureLog,
    shortfallByBlock: Object.fromEntries(shortfallByBlock),
    failureCountByBlock: Object.fromEntries(failureCountByBlock),
    downtimeByBlock: Object.fromEntries(downtimeByBlock),
    blockHistory: Object.fromEntries(blockHistory)
  };
}

// ---- Monte Carlo Runner ----
function runMonteCarlo(blocks, connections, config, numRuns = 1000, onProgress = null) {
  const results = [];
  const allShortfallByBlock = {};
  const allFailureCountByBlock = {};
  const allDowntimeByBlock = {};

  blocks.forEach(b => {
    allShortfallByBlock[b.id] = [];
    allFailureCountByBlock[b.id] = [];
    allDowntimeByBlock[b.id] = [];
  });

  for (let run = 0; run < numRuns; run++) {
    const result = simulateLifecycle(blocks, connections, config);
    results.push(result);

    blocks.forEach(b => {
      allShortfallByBlock[b.id].push(result.shortfallByBlock[b.id] || 0);
      allFailureCountByBlock[b.id].push(result.failureCountByBlock[b.id] || 0);
      allDowntimeByBlock[b.id].push(result.downtimeByBlock[b.id] || 0);
    });

    if (onProgress && run % 50 === 0) {
      onProgress(run / numRuns);
    }
  }

  // Statistical analysis
  const availabilities = results.map(r => r.productionAvailability);
  const productions = results.map(r => r.totalProduction);
  const shortfalls = results.map(r => r.shortfall);

  availabilities.sort((a, b) => a - b);
  productions.sort((a, b) => a - b);
  shortfalls.sort((a, b) => a - b);

  const percentile = (arr, p) => {
    const idx = Math.floor(arr.length * p);
    return arr[Math.min(idx, arr.length - 1)];
  };
  const mean = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const stddev = (arr) => {
    const m = mean(arr);
    return Math.sqrt(arr.reduce((s, v) => s + (v - m) * (v - m), 0) / arr.length);
  };

  // Shortfall contributors (system-level Pareto)
  const shortfallContributors = blocks.map(b => {
    const blockShortfalls = allShortfallByBlock[b.id];
    const meanShortfall = mean(blockShortfalls);
    const meanFailures = mean(allFailureCountByBlock[b.id]);
    const meanDowntime = mean(allDowntimeByBlock[b.id]);
    return {
      blockId: b.id,
      description: b.description || b.id,
      equipmentType: b.equipmentType || '',
      meanShortfall,
      meanFailures,
      meanDowntime,
      percentOfTotal: 0 // computed below
    };
  }).sort((a, b) => b.meanShortfall - a.meanShortfall);

  const totalMeanShortfall = mean(shortfalls);
  shortfallContributors.forEach(sc => {
    sc.percentOfTotal = totalMeanShortfall > 0 ? (sc.meanShortfall / totalMeanShortfall) * 100 : 0;
  });

  // Add shutdown/bean-up as contributors
  const shutdownShortfall = mean(results.map(r => r.shutdownShortfall));

  // Monthly breakdown (for 8760h mission = 12 months)
  const monthlyBreakdown = computeMonthlyBreakdown(results, config);

  // Top failing equipment
  const topFailingEquipment = [...shortfallContributors]
    .sort((a, b) => b.meanFailures - a.meanFailures)
    .slice(0, 15);

  // P50 representative run (median production availability)
  const medianIdx = Math.floor(results.length / 2);
  const sortedByAvail = [...results].sort((a, b) => a.productionAvailability - b.productionAvailability);
  const p50Run = sortedByAvail[medianIdx];

  return {
    numRuns,
    missionTime: config.missionTime || 8760,
    targetProduction: config.targetProduction || 100,
    productionUnit: config.productionUnit || 'bbl',

    // Production Availability
    availability: {
      mean: mean(availabilities),
      p5: percentile(availabilities, 0.95),    // 5% chance of exceeding this
      p50: percentile(availabilities, 0.50),
      p95: percentile(availabilities, 0.05),   // 95% chance of exceeding this
      stddev: stddev(availabilities),
      distribution: availabilities
    },

    // Production volume
    production: {
      mean: mean(productions),
      p5: percentile(productions, 0.95),
      p50: percentile(productions, 0.50),
      p95: percentile(productions, 0.05),
      target: results[0]?.totalTarget || 0
    },

    // Shortfall
    shortfall: {
      mean: totalMeanShortfall,
      p5: percentile(shortfalls, 0.05),
      p95: percentile(shortfalls, 0.95),
      shutdownContribution: shutdownShortfall
    },

    // Contributors (Pareto)
    shortfallContributors,
    topFailingEquipment,

    // Monthly
    monthlyBreakdown,

    // Representative run
    p50Run,

    // Raw for custom analysis
    allResults: results
  };
}

// ---- Monthly Breakdown ----
function computeMonthlyBreakdown(results, config) {
  const missionTime = config.missionTime || 8760;
  const targetProduction = config.targetProduction || 100;
  const timeStep = config.timeStep || 1;
  const stepsPerHour = 1 / timeStep;

  // Approximate months (730.5 hours each for 8760/12)
  const hoursPerMonth = missionTime / 12;
  const stepsPerMonth = Math.ceil(hoursPerMonth / timeStep);

  const months = [];
  for (let m = 0; m < 12; m++) {
    const startStep = m * stepsPerMonth;
    const endStep = Math.min((m + 1) * stepsPerMonth, Math.ceil(missionTime / timeStep));

    const monthTarget = targetProduction * (endStep - startStep) * timeStep;
    const monthProductions = results.map(r => {
      let prod = 0;
      for (let s = startStep; s < endStep && s < r.hourlyProduction.length; s++) {
        prod += r.hourlyProduction[s];
      }
      return prod;
    });

    monthProductions.sort((a, b) => a - b);
    const mean_val = monthProductions.reduce((s, v) => s + v, 0) / monthProductions.length;

    months.push({
      month: m,
      monthName: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][m],
      target: monthTarget,
      meanProduction: mean_val,
      availability: monthTarget > 0 ? mean_val / monthTarget : 1,
      shortfall: monthTarget - mean_val
    });
  }

  return months;
}

// ---- Sensitivity Analysis ----
function runSensitivity(blocks, connections, baseConfig, sensitivityDef, numRuns = 500) {
  // sensitivityDef: { parameter: 'logisticDelay', values: [0, 24, 168, 720, 1440, 2160, 2880], label: 'Logistic Delay (hours)' }
  // OR { parameter: 'blockMTBF', blockId: 'xxx', multipliers: [0.5, 0.75, 1.0, 1.25, 1.5, 2.0] }
  // OR { parameter: 'removeShutdown', shutdownIdx: 0 }
  // OR { parameter: 'addRedundancy', blockId: 'xxx' }

  const results = [];
  const param = sensitivityDef.parameter;

  if (param === 'logisticDelay') {
    (sensitivityDef.values || [0, 24, 168, 720, 1440]).forEach(delay => {
      const cfg = {
        ...baseConfig,
        logisticDelayConfig: { probability: sensitivityDef.probability || 0.1, durations: [delay] }
      };
      const mc = runMonteCarlo(blocks, connections, cfg, numRuns);
      results.push({
        label: `${delay}h delay`,
        value: delay,
        availability: mc.availability,
        shortfall: mc.shortfall,
        shortfallContributors: mc.shortfallContributors
      });
    });
  } else if (param === 'blockMTBF') {
    const origBlock = blocks.find(b => b.id === sensitivityDef.blockId);
    if (origBlock) {
      (sensitivityDef.multipliers || [0.5, 1.0, 2.0]).forEach(mult => {
        const modBlocks = blocks.map(b =>
          b.id === sensitivityDef.blockId ? { ...b, mtbf: (b.mtbf || 0) * mult } : b
        );
        const mc = runMonteCarlo(modBlocks, connections, baseConfig, numRuns);
        results.push({
          label: `${origBlock.description} MTBF ×${mult}`,
          value: mult,
          availability: mc.availability,
          shortfall: mc.shortfall
        });
      });
    }
  } else if (param === 'removeShutdown') {
    // Run without the specified shutdown
    const cfg = { ...baseConfig };
    const sdIdx = sensitivityDef.shutdownIdx || 0;
    cfg.fullShutdowns = (baseConfig.fullShutdowns || []).filter((_, i) => i !== sdIdx);
    const mc = runMonteCarlo(blocks, connections, cfg, numRuns);
    results.push({
      label: 'Without Planned Shutdown',
      availability: mc.availability,
      shortfall: mc.shortfall,
      shortfallContributors: mc.shortfallContributors
    });
  } else if (param === 'custom') {
    // User-defined scenarios
    (sensitivityDef.scenarios || []).forEach(scenario => {
      const modBlocks = blocks.map(b => {
        const override = scenario.blockOverrides?.[b.id];
        return override ? { ...b, ...override } : b;
      });
      const cfg = { ...baseConfig, ...scenario.configOverrides };
      const mc = runMonteCarlo(modBlocks, connections, cfg, numRuns);
      results.push({
        label: scenario.label || 'Custom',
        availability: mc.availability,
        shortfall: mc.shortfall,
        shortfallContributors: mc.shortfallContributors
      });
    });
  }

  return { parameter: param, label: sensitivityDef.label || param, results };
}

// ---- Asset Resilience Analysis ----
function analyseResilience(blocks, connections, mcResults) {
  const findings = [];

  // 1. Single points of failure (no redundancy)
  blocks.forEach(b => {
    if (b.blockType === 'series' || !b.blockType) {
      const shortfallData = mcResults.shortfallContributors.find(sc => sc.blockId === b.id);
      if (shortfallData && shortfallData.meanShortfall > 0) {
        findings.push({
          type: 'single_point_of_failure',
          severity: shortfallData.percentOfTotal > 2 ? 'high' : shortfallData.percentOfTotal > 0.5 ? 'medium' : 'low',
          blockId: b.id,
          description: b.description || b.id,
          impact: `${shortfallData.percentOfTotal.toFixed(1)}% of total production shortfall`,
          shortfall: shortfallData.meanShortfall,
          recommendation: `Consider adding standby redundancy for ${b.description || b.id}. Mean ${shortfallData.meanFailures.toFixed(1)} failures/year with ${shortfallData.meanDowntime.toFixed(0)}h mean downtime.`
        });
      }
    }
  });

  // 2. High-failure-frequency equipment
  const highFailers = mcResults.topFailingEquipment.filter(t => t.meanFailures > 2);
  highFailers.forEach(hf => {
    const block = blocks.find(b => b.id === hf.blockId);
    findings.push({
      type: 'high_failure_frequency',
      severity: hf.meanFailures > 5 ? 'high' : 'medium',
      blockId: hf.blockId,
      description: hf.description,
      impact: `Mean ${hf.meanFailures.toFixed(1)} failures/year`,
      recommendation: `Review maintenance strategy for ${hf.description}. Current MTBF: ${block?.mtbf || 'N/A'}h. Consider condition monitoring or proactive replacement.`
    });
  });

  // 3. High MTTR items (>48h mean downtime per event)
  blocks.forEach(b => {
    if (b.mttr && b.mttr > 48) {
      const shortfallData = mcResults.shortfallContributors.find(sc => sc.blockId === b.id);
      findings.push({
        type: 'high_repair_time',
        severity: b.mttr > 168 ? 'high' : 'medium',
        blockId: b.id,
        description: b.description || b.id,
        impact: `MTTR: ${b.mttr}h (${(b.mttr / 24).toFixed(1)} days)`,
        recommendation: `Review sparing and logistics for ${b.description || b.id}. Pre-positioning critical spares could reduce MTTR significantly.`
      });
    }
  });

  // 4. Logistic delay vulnerability
  blocks.forEach(b => {
    if (b.logisticDelay && b.logisticDelay > 168) { // >1 week
      findings.push({
        type: 'logistic_vulnerability',
        severity: 'high',
        blockId: b.id,
        description: b.description || b.id,
        impact: `Logistic delay: ${b.logisticDelay}h (${(b.logisticDelay / 24).toFixed(0)} days)`,
        recommendation: `Establish local spares holding or service agreement for ${b.description || b.id} to reduce logistic delay.`
      });
    }
  });

  // Sort by severity
  const severityOrder = { high: 0, medium: 1, low: 2 };
  findings.sort((a, b) => (severityOrder[a.severity] || 9) - (severityOrder[b.severity] || 9));

  return {
    findings,
    summary: {
      singlePoints: findings.filter(f => f.type === 'single_point_of_failure').length,
      highFailers: findings.filter(f => f.type === 'high_failure_frequency').length,
      highMTTR: findings.filter(f => f.type === 'high_repair_time').length,
      logisticRisks: findings.filter(f => f.type === 'logistic_vulnerability').length,
      totalFindings: findings.length,
      highSeverity: findings.filter(f => f.severity === 'high').length,
      mediumSeverity: findings.filter(f => f.severity === 'medium').length,
      lowSeverity: findings.filter(f => f.severity === 'low').length
    }
  };
}

// ---- Report Data Generator ----
function generateRAMReportData(blocks, connections, mcResults, sensitivityResults, resilienceResults, studyConfig) {
  return {
    metadata: {
      title: studyConfig.title || 'RAM Availability Study',
      client: studyConfig.client || '',
      facility: studyConfig.facility || '',
      reportNumber: studyConfig.reportNumber || `OPT-RAM-${Date.now()}`,
      revision: studyConfig.revision || '1',
      date: new Date().toISOString().split('T')[0],
      author: studyConfig.author || 'Optimal Asset Management SA',
      software: 'OptimalAvailability Studio',
      methodology: 'Monte Carlo RBD Simulation'
    },
    studyConfig: {
      missionTime: studyConfig.missionTime || 8760,
      numRuns: mcResults.numRuns,
      targetProduction: studyConfig.targetProduction,
      productionUnit: studyConfig.productionUnit || 'bbl',
      equipmentCount: blocks.length,
      systemDescription: studyConfig.systemDescription || ''
    },
    reliabilityAssetRegister: blocks.map(b => ({
      id: b.id,
      description: b.description || '',
      equipmentType: b.equipmentType || '',
      blockType: b.blockType || 'series',
      mtbf: b.mtbf || 0,
      mttr: b.mttr || 0,
      distribution: b.distribution || 'exponential',
      beta: b.beta || null,
      eta: b.eta || null,
      capacity: b.capacity || 100,
      logisticDelay: b.logisticDelay || 0,
      pmInterval: b.pmInterval || 0,
      pmDuration: b.pmDuration || 0,
      redundantGroup: b.redundantGroup || '',
      dataSource: b.dataSource || 'Optimal Reference Library'
    })),
    availability: mcResults.availability,
    production: mcResults.production,
    shortfall: mcResults.shortfall,
    shortfallContributors: mcResults.shortfallContributors,
    topFailingEquipment: mcResults.topFailingEquipment,
    monthlyBreakdown: mcResults.monthlyBreakdown,
    sensitivityResults: sensitivityResults || [],
    resilience: resilienceResults || { findings: [], summary: {} }
  };
}

// Exports
export {
  runMonteCarlo,
  runSensitivity,
  analyseResilience,
  generateRAMReportData,
  simulateLifecycle,
  evaluateRBD,
  sampleTTF,
  sampleTTR
};
